import { Input } from "design-system/atoms/Input"
import { Label } from "design-system/atoms/Label"
import { Switch } from "design-system/atoms/Switch"
import { Textarea } from "design-system/atoms/Textarea"
import { Loading } from "modules/shared/components/Loading"
import { useCallback, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import {
  CapabilitiesResponseDataSchema,
  CapabilityCampaignAssignation,
  CapabilityResponseDataSchema,
} from "schema"
import { toast } from "sonner"
import z from "zod"
import { useCapabilityProvider } from "../../hooks/useCapabilityProvider"
import { useInstallCapabilityMutation } from "../../hooks/useInstallCapabilityMutation"
import { useUpdateCapabilityConfigMutation } from "../../hooks/useUpdateCapabilityConfigMutation"
import { CapabilityOrganizationPhoneNumbers } from "./CapabilityOrganizationPhoneNumbers"

type Props = {
  capability: z.infer<typeof CapabilitiesResponseDataSchema>[number]
  mode?: "install" | "update"
  initialConfig?: {
    shouldAutoRefund?: boolean
    shouldRestock?: boolean
    cancellationWindowMinutes?: number | null
    organizationPhoneNumbers?: string | null
    activationInstructions?: string
    responseInstructions?: string
  }
  onClose: () => void
  onPendingChange?: (isPending: boolean) => void
  onConfigure?: (capability: z.infer<typeof CapabilityResponseDataSchema>) => void
  onSubmitHandlerRef?: React.MutableRefObject<
    | ((
        campaignAssignation: CapabilityCampaignAssignation,
        campaignIds: string[],
        campaignFlow?: "Inbound" | "Outbound",
      ) => Promise<void>)
    | null
  >
}

export function CancelOrderForm({
  capability,
  mode = "install",
  initialConfig,
  onClose,
  onPendingChange,
  onConfigure,
  onSubmitHandlerRef,
}: Props) {
  const { t } = useTranslation()
  const { mutateAsync: installCapability, isPending: isInstallPending } =
    useInstallCapabilityMutation()
  const { mutateAsync: updateCapabilityConfig, isPending: isUpdatePending } =
    useUpdateCapabilityConfigMutation()

  const isPending = mode === "install" ? isInstallPending : isUpdatePending

  const { isIntegrationsStatusPending, organizationPhoneNumbers, setOrganizationPhoneNumbers } =
    useCapabilityProvider({
      capability,
      mode,
      initialConfig,
    })

  const [shouldAutoRefund, setShouldAutoRefund] = useState(initialConfig?.shouldAutoRefund ?? true)
  const [shouldRestock, setShouldRestock] = useState(initialConfig?.shouldRestock ?? true)
  const [cancellationWindowMinutes, setCancellationWindowMinutes] = useState<string>(
    initialConfig?.cancellationWindowMinutes != null
      ? String(initialConfig.cancellationWindowMinutes)
      : "",
  )
  const [activationInstructions, setActivationInstructions] = useState(
    initialConfig?.activationInstructions ?? "",
  )
  const [responseInstructions, setResponseInstructions] = useState(
    initialConfig?.responseInstructions ?? "",
  )

  useEffect(() => {
    onPendingChange?.(isPending || isIntegrationsStatusPending)
  }, [isPending, isIntegrationsStatusPending, onPendingChange])

  useEffect(() => {
    setActivationInstructions(initialConfig?.activationInstructions ?? "")
  }, [initialConfig?.activationInstructions])

  useEffect(() => {
    setResponseInstructions(initialConfig?.responseInstructions ?? "")
  }, [initialConfig?.responseInstructions])

  const handleSubmit = useCallback(
    async (
      campaignAssignation: CapabilityCampaignAssignation,
      campaignIds: string[],
      campaignFlow?: "Inbound" | "Outbound",
    ) => {
      if (isPending) return

      const config = {
        shouldAutoRefund,
        shouldRestock,
        cancellationWindowMinutes: cancellationWindowMinutes
          ? parseInt(cancellationWindowMinutes, 10)
          : null,
        organizationPhoneNumbers,
        activationInstructions,
        responseInstructions,
      }

      let installedCapability: z.infer<typeof CapabilityResponseDataSchema> | undefined

      if (mode === "install") {
        installedCapability = await installCapability({
          identifier: capability.identifier,
          campaignAssignation,
          config,
          campaignIds,
          campaignFlow,
        })

        toast.success(
          t("capabilities.list.install.success", {
            capability: t(`capabilities.catalog.${capability.identifier}.name`),
          }),
        )
      } else {
        await updateCapabilityConfig({
          id: capability.id,
          config,
        })
      }

      if (onConfigure && installedCapability) {
        onConfigure(installedCapability)
      } else {
        onClose()
      }
    },
    [
      isPending,
      mode,
      onConfigure,
      installCapability,
      capability.identifier,
      capability.id,
      shouldAutoRefund,
      shouldRestock,
      cancellationWindowMinutes,
      t,
      updateCapabilityConfig,
      onClose,
      organizationPhoneNumbers,
      activationInstructions,
      responseInstructions,
    ],
  )

  if (onSubmitHandlerRef) {
    onSubmitHandlerRef.current = handleSubmit
  }

  if (isIntegrationsStatusPending) return <Loading />

  return (
    <div className="flex flex-col gap-4">
      <p className="text-sm text-muted-foreground">
        {t(`capabilities.catalog.${capability.identifier}.config.providersDescription`)}
      </p>

      <div className="flex items-center justify-between gap-2">
        <div className="flex flex-col gap-0.5">
          <Label htmlFor="should-auto-refund">
            {t(`capabilities.catalog.${capability.identifier}.config.shouldAutoRefund`)}
          </Label>
          <p className="text-sm text-muted-foreground">
            {t(`capabilities.catalog.${capability.identifier}.config.shouldAutoRefundDescription`)}
          </p>
        </div>
        <Switch
          id="should-auto-refund"
          checked={shouldAutoRefund}
          onCheckedChange={setShouldAutoRefund}
        />
      </div>

      {shouldAutoRefund && (
        <div className="flex items-center justify-between gap-2">
          <div className="flex flex-col gap-0.5">
            <Label htmlFor="should-restock">
              {t(`capabilities.catalog.${capability.identifier}.config.shouldRestock`)}
            </Label>
            <p className="text-sm text-muted-foreground">
              {t(`capabilities.catalog.${capability.identifier}.config.shouldRestockDescription`)}
            </p>
          </div>
          <Switch id="should-restock" checked={shouldRestock} onCheckedChange={setShouldRestock} />
        </div>
      )}

      <div className="flex flex-col gap-0.5">
        <Label htmlFor="cancellation-window">
          {t(`capabilities.catalog.${capability.identifier}.config.cancellationWindowMinutes`)}
        </Label>
        <Input
          id="cancellation-window"
          type="number"
          min={1}
          value={cancellationWindowMinutes}
          onChange={(e) => setCancellationWindowMinutes(e.target.value)}
          placeholder={t(
            `capabilities.catalog.${capability.identifier}.config.cancellationWindowMinutesPlaceholder`,
          )}
        />
        <p className="text-sm text-muted-foreground">
          {t(
            `capabilities.catalog.${capability.identifier}.config.cancellationWindowMinutesDescription`,
          )}
        </p>
      </div>
      <div className="flex flex-col gap-1.5">
        <Label>
          {t(`capabilities.catalog.${capability.identifier}.config.activationInstructions.label`)}
        </Label>
        <Textarea
          value={activationInstructions}
          onChange={(ev) => setActivationInstructions(ev.target.value)}
          placeholder={t(
            `capabilities.catalog.${capability.identifier}.config.activationInstructions.placeholder`,
          )}
          rows={5}
          maxLength={2048}
        />
        <p className="text-sm text-muted-foreground">
          {t(
            `capabilities.catalog.${capability.identifier}.config.activationInstructions.description`,
          )}
        </p>
      </div>
      <div className="flex flex-col gap-1.5">
        <Label>
          {t(`capabilities.catalog.${capability.identifier}.config.responseInstructions.label`)}
        </Label>
        <Textarea
          value={responseInstructions}
          onChange={(ev) => setResponseInstructions(ev.target.value)}
          placeholder={t(
            `capabilities.catalog.${capability.identifier}.config.responseInstructions.placeholder`,
          )}
          rows={5}
          maxLength={2048}
        />
        <p className="text-sm text-muted-foreground">
          {t(
            `capabilities.catalog.${capability.identifier}.config.responseInstructions.description`,
          )}
        </p>
      </div>
      <CapabilityOrganizationPhoneNumbers
        organizationPhoneNumbers={organizationPhoneNumbers}
        setOrganizationPhoneNumbers={setOrganizationPhoneNumbers}
      />
    </div>
  )
}
